// useCoach: the signed-in person's built-in coach, shared by the shell
// presence, the coach panel, the coach page and any compact brief.
//
// One module store per tab, so the presence, the panel and a page that all
// show the coach make one request between them and agree on the counts.
//
//   const coach = useCoach();              // loads the companion on mount
//   const coach = useCoach({ load: false }); // read the store only
//
// Returns:
//   data            companion payload (brief, plan, recap, progress, history,
//                   managerNotes, attention, demo, person, today) or null
//   status          "idle" | "loading" | "ready" | "error"
//   error           message when status is "error"
//   refresh()       reload the companion (and the presence counts)
//   presence        {callbacks, overdue, followups, draftsPending, total} or null
//   thinking        true while the assistant is working on a run
//   followups       {items, demo, sending} or null
//   followupsStatus "idle" | "loading" | "ready" | "error"
//   followupsError  message when followupsStatus is "error"
//   loadFollowups() load or reload the follow-up queue
//   draft(attrs)    draft a follow-up ({contactId, channel, body?, subject?,
//                   replacesActionId?}); resolves to the new assistant action
//   decide(id, "approve" | "reject")  approve or discard a draft through the
//                   assistant action routes; resolves to the updated action
//
// Nothing here sends a message by itself: a draft is an assistant action
// that waits for the person's approval, and the server refuses to send in
// demo organizations or without a configured email sender.

import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useAuth } from "../contexts/AuthContext";
import coachCompanionApi from "../services/coachCompanionApi";
import assistantApi from "../services/ai";

const initial = {
  data: null,
  status: "idle",
  error: null,
  presence: null,
  thinking: false,
  followups: null,
  followupsStatus: "idle",
  followupsError: null,
};

let state = initial;
let owner;
const listeners = new Set();
let companionRequest = null;
let followupsRequest = null;

const set = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const snapshot = () => state;

/** Reset between signed-in people and between tests. */
export const resetCoachStore = () => {
  owner = undefined;
  companionRequest = null;
  followupsRequest = null;
  state = initial;
  listeners.forEach((listener) => listener());
};

/** The assistant panel says when a run starts and stops working. */
export const setCoachThinking = (thinking) => {
  if (state.thinking !== Boolean(thinking))
    set({ thinking: Boolean(thinking) });
};

export const loadCompanion = ({ force = false } = {}) => {
  if (companionRequest && !force) return companionRequest;
  set({ status: state.data ? state.status : "loading", error: null });
  const request = coachCompanionApi
    .getCompanion()
    .then((data) => {
      if (companionRequest !== request) return state.data;
      set({
        data,
        status: "ready",
        error: null,
        presence: data?.attention ?? state.presence,
      });
      return data;
    })
    .catch((error) => {
      if (companionRequest === request) {
        set({ status: state.data ? "ready" : "error", error: error.message });
      }
      return null;
    });
  companionRequest = request;
  return request;
};

export const loadPresence = () => {
  const requestedFor = owner;
  return coachCompanionApi
    .getPresence()
    .then((presence) => {
      if (owner === requestedFor) set({ presence });
      return presence;
    })
    .catch(() => state.presence);
};

export const loadFollowups = ({ force = true } = {}) => {
  if (followupsRequest && !force) return followupsRequest;
  set({
    followupsStatus: state.followups ? state.followupsStatus : "loading",
    followupsError: null,
  });
  const request = coachCompanionApi
    .getFollowups()
    .then((followups) => {
      if (followupsRequest === request) {
        set({ followups, followupsStatus: "ready", followupsError: null });
      }
      return followups;
    })
    .catch((error) => {
      if (followupsRequest === request) {
        set({
          followupsStatus: state.followups ? "ready" : "error",
          followupsError: error.message,
        });
      }
      return null;
    });
  followupsRequest = request;
  return request;
};

const afterChange = () => {
  loadFollowups();
  loadPresence();
};

export const draftFollowup = async (attrs) => {
  const action = await coachCompanionApi.draftFollowup(attrs);
  afterChange();
  return action;
};

export const decideFollowup = async (actionId, decision) => {
  const action =
    decision === "approve"
      ? await assistantApi.approveAction(actionId)
      : await assistantApi.rejectAction(actionId);
  afterChange();
  return action;
};

// A different signed-in person never sees the previous person's coach.
// Runs during render, so it resets without notifying: every consumer reads
// the same auth context and re-renders with the fresh snapshot.
const claim = (userId) => {
  if (owner !== undefined && owner !== userId) {
    companionRequest = null;
    followupsRequest = null;
    state = initial;
  }
  owner = userId;
};

export const useCoach = ({ load = true } = {}) => {
  const userId = useAuth()?.user?.id ?? null;
  claim(userId);
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);

  useEffect(() => {
    if (load && current.status === "idle") loadCompanion();
  }, [load, current.status]);

  const refresh = useCallback(() => {
    loadPresence();
    return loadCompanion({ force: true });
  }, []);

  return {
    ...current,
    refresh,
    loadFollowups,
    draft: draftFollowup,
    decide: decideFollowup,
  };
};

export default useCoach;
